export * from './const';
export * from './field-classification';

export type DateInput = string | Date | null | undefined;

export function isValidDate(value: unknown): value is Date {
  return value instanceof Date && !Number.isNaN(value.getTime());
}

export function formatDateForApi(date: Date): string | null {
  if (!isValidDate(date)) {
    return null;
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatDateToPtBR(date: Date | null | undefined): string {
  if (!date || !isValidDate(date)) {
    return '';
  }
  return date.toLocaleDateString('pt-BR');
}

/**
 * Canonical display helper for civil dates. Accepts a civil-date string
 * (`YYYY-MM-DD`), a `Date`, or null/undefined and returns `DD/MM/YYYY`.
 *
 * Strings are parsed with `parseCivilDate` (never `new Date(string)`, which
 * off-by-ones civil dates across time zones). Returns `'-'` for missing or
 * invalid input. This is the single shared version that replaces per-site
 * re-implementations (e.g. ViewNotificationModal.vue).
 */
export function formatCivilDateToPtBR(value: DateInput): string {
  if (!value) return '-';
  const date = typeof value === 'string' ? parseCivilDate(value) : value;
  if (!isValidDate(date)) return '-';
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

const PREFORMATTED_DATETIME_REGEX = /^\d{2}\/\d{2}\/\d{4} \| \d{2}:\d{2}$/;

/**
 * Formats an instant (ISO 8601 string or Date) for pt-BR display as
 * `DD/MM/YYYY | HH:mm` in the São Paulo time zone (`America/Sao_Paulo`),
 * the system's authoritative zone, so every viewer sees the same time
 * regardless of their browser's local zone.
 *
 * The pre-formatted-string branch is defensive: no live API endpoint emits
 * `DD/MM/YYYY | HH:mm` after the backend `date-formatter` middleware was
 * removed in HTMDOC-1718, but cached responses, persisted state, or offline
 * fixtures may still surface that shape. Keep the branch until those sources
 * are confirmed clean.
 *
 * Pass `{ withTime: false }` to render the date only (`DD/MM/YYYY`), still in
 * São Paulo — use this instead of `formatApiDateStringToPtBR` for instants,
 * which would off-by-one near midnight UTC by parsing the ISO date portion.
 */
export function formatInstantToPtBR(
  value: string | Date | null | undefined,
  options: { withTime?: boolean } = {}
): string {
  const { withTime = true } = options;
  if (!value) return '-';
  if (typeof value === 'string' && PREFORMATTED_DATETIME_REGEX.test(value)) {
    return withTime ? value : value.split(' | ')[0];
  }
  const d = value instanceof Date ? value : new Date(value);
  if (!isValidDate(d)) return '-';
  const date = d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'America/Sao_Paulo',
  });
  if (!withTime) return date;
  const time = d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });
  return `${date} | ${time}`;
}

/**
 * Formats an instant (ISO 8601 string or Date) for date-only pt-BR display as
 * `DD/MM/YYYY` in the São Paulo time zone (`America/Sao_Paulo`), dropping the
 * time component. Use when only the calendar day of an instant matters (e.g.
 * "Data de salvamento"). Returns `'-'` for missing or invalid input.
 */
export function formatInstantDateToPtBR(value: DateInput): string {
  if (!value) return '-';
  if (typeof value === 'string' && PREFORMATTED_DATETIME_REGEX.test(value)) {
    return value.split(' | ')[0];
  }
  const d = value instanceof Date ? value : new Date(value);
  if (!isValidDate(d)) return '-';
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'America/Sao_Paulo',
  });
}

const SAO_PAULO_CIVIL_DAY_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Sao_Paulo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/**
 * Projects an instant (ISO 8601 string or Date) onto the civil date
 * (`YYYY-MM-DD`) of the São Paulo calendar day it falls on. Use this — never a
 * `.slice(0, 10)` of the ISO string, which yields the UTC day and off-by-ones
 * for anything after 21:00 in São Paulo.
 *
 * This is the comparison key for "which day did this happen on"; pair it with
 * `formatDateForApi` on the civil side. `formatInstantDateToPtBR` is the
 * display counterpart and must not be used for comparisons — its `DD/MM/YYYY`
 * shape depends on the runtime's ICU data. Returns `null` for missing or
 * invalid input.
 */
export function instantToSaoPauloCivilDate(value: DateInput): string | null {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (!isValidDate(d)) return null;
  return SAO_PAULO_CIVIL_DAY_FORMATTER.format(d);
}

/**
 * Formats an API date string for date-only pt-BR display (`DD/MM/YYYY`),
 * dropping any time component. Parses the input with `stringToDate` (handles
 * `YYYY-MM-DD`, ISO, `DD/MM/YYYY` and `DD/MM/YYYY | HH:mm`) and returns
 * `fallback` for empty/invalid input.
 */
export function formatApiDateStringToPtBR(
  dateString: string | null | undefined,
  fallback = ''
): string {
  if (!dateString?.trim()) {
    return fallback;
  }

  const parsed = stringToDate(dateString);
  return parsed ? formatDateToPtBR(parsed) : fallback;
}

function parseDateFromSlashFormat(dateString: string): Date | null {
  if (!dateString.includes('/')) return null;

  const parts = dateString.split('/');
  if (parts.length !== 3) return null;

  const day = Number.parseInt(parts[0], 10);
  const month = Number.parseInt(parts[1], 10) - 1;
  const year = Number.parseInt(parts[2], 10);
  const date = new Date(year, month, day);

  return isValidDate(date) ? date : null;
}

function parseDateFromDashFormat(dateString: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return null;

  const [year, month, day] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);

  if (!isValidDate(date)) return null;
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
}

/**
 * Parses a strict civil-date string (`YYYY-MM-DD`) into a `Date` at the
 * browser's local midnight. Rejects malformed strings and invalid calendar
 * days (e.g., `2026-02-31`). Round-trips with `formatDateForApi`:
 * `formatDateForApi(parseCivilDate(s)) === s` for any valid civil date.
 *
 * Use this for civil-date API fields (validityDate, effectiveDate,
 * admissionDate, dismissalDate, startDate, endDate, customDates[].date).
 * For ISO instants, use `new Date(iso)` directly.
 */
export function parseCivilDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  return parseDateFromDashFormat(value);
}

function parseDateFromIsoFormat(dateString: string): Date | null {
  if (!dateString.includes('T')) return null;

  const [datePart] = dateString.split('T');
  return parseDateFromDashFormat(datePart);
}

function parseDateFromPtBRDateTime(dateString: string): Date | null {
  if (!dateString.includes(' | ')) return null;

  const [datePart] = dateString.split(' | ');
  return parseDateFromSlashFormat(datePart);
}

/**
 * Converte string de data (DD/MM/YYYY ou ISO) para Date object
 * @param dateString - String de data em formato DD/MM/YYYY ou ISO, Date object, undefined ou null
 * @returns Date object ou null se inválido
 */
export function stringToDate(dateString: DateInput): Date | null {
  if (!dateString) return null;

  if (dateString instanceof Date) {
    return isValidDate(dateString) ? dateString : null;
  }

  try {
    const parsedDate =
      parseDateFromPtBRDateTime(dateString) ??
      parseDateFromSlashFormat(dateString) ??
      parseDateFromDashFormat(dateString) ??
      parseDateFromIsoFormat(dateString);
    if (parsedDate) return parsedDate;

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return null;

    const date = new Date(dateString);
    return isValidDate(date) ? date : null;
  } catch {
    return null;
  }
}
